// ui-kit: hand-built
import {
    Children,
    cloneElement,
    isValidElement,
    useCallback,
    useEffect,
    useId,
    useRef,
    type ReactElement,
    type ReactNode,
    type Ref,
} from 'react';
import { Tooltip as ReactTooltip, type PlacesType, type TooltipRefProps } from 'react-tooltip';

import { classNames } from '../../internal/classNames';
import { mergeRefs } from '../../internal/mergeRefs';

export type TooltipPlace = PlacesType;

export type TooltipProps = {
    children: ReactElement<{ ref?: Ref<HTMLElement> }>;
    content?: ReactNode;
    place?: TooltipPlace;
    /** When false, matches Figma Arrow=No. */
    arrow?: boolean;
    clickable?: boolean;
    delayShow?: number;
    delayHide?: number;
    /** Extra class on the tooltip bubble (not the trigger). */
    className?: string;
    disabled?: boolean;
    /** Only open when the trigger's content is truncated (measured on hover). */
    overflowOnly?: boolean;
};

function Tooltip({
    children,
    content,
    place = 'bottom',
    arrow = true,
    clickable = false,
    delayShow,
    delayHide,
    className = '',
    disabled = false,
    overflowOnly = false,
}: TooltipProps) {
    const reactId = useId().replace(/:/g, '');
    const tooltipId = `ui-tooltip-${reactId}`;
    const tooltipRef = useRef<TooltipRefProps>(null);
    const anchorRef = useRef<HTMLElement>(null);

    const child = Children.only(children);
    if (!isValidElement(child)) {
        throw new Error('Tooltip children must be a single React element');
    }

    const showTooltip = Boolean(content) && !disabled;
    const disableTooltip = useCallback(
        (anchor: Element | null) =>
            isFocusedWithoutFocusVisible(anchor) || (overflowOnly && !isOverflowing(anchor)),
        [overflowOnly],
    );

    useEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor || !showTooltip) return;

        const controller = new AbortController();
        anchor.addEventListener(
            'click',
            () => {
                tooltipRef.current?.close();
            },
            { signal: controller.signal },
        );

        return () => {
            controller.abort();
        };
    }, [showTooltip, child]);

    const trigger = cloneElement(child, {
        ref: mergeRefs(anchorRef, child.props.ref),
        ...(showTooltip
            ? {
                  'data-tooltip-id': tooltipId,
                  'data-tooltip-place': place,
                  'data-tooltip-position-strategy': 'fixed' as const,
              }
            : {}),
    });

    if (!showTooltip) {
        return trigger;
    }

    return (
        <>
            {trigger}
            <ReactTooltip
                ref={tooltipRef}
                id={tooltipId}
                content={content}
                place={place}
                positionStrategy='fixed'
                // Without a portal root, react-tooltip mounts inline next to the
                // trigger and can shift flex/centered layouts (e.g. Storybook).
                portalRoot={typeof document !== 'undefined' ? document.body : null}
                noArrow={!arrow}
                clickable={clickable}
                delayShow={delayShow}
                delayHide={delayHide}
                disableTooltip={disableTooltip}
                opacity={1}
                disableStyleInjection='core'
                className={classNames({
                    'ui-tooltip': true,
                    'ui-tooltip--clickable': clickable,
                    'ui-tooltip--no-arrow': !arrow,
                    [className]: Boolean(className),
                })}
                classNameArrow='ui-tooltip__arrow'
            />
        </>
    );
}

Tooltip.displayName = 'Tooltip';

export default Tooltip;

// scroll* and client* round subpixel sizes differently, so a 1px gap is not overflow.
function isOverflowing(el: Element | null) {
    return (
        el !== null &&
        (el.scrollWidth - el.clientWidth > 1 || el.scrollHeight - el.clientHeight > 1)
    );
}

// True for pointer or script focus (not `:focus-visible`) on an anchor that is not hovered.
function isFocusedWithoutFocusVisible(el: Element | null) {
    return (
        el !== null &&
        el === document.activeElement &&
        !el.matches(':focus-visible') &&
        !el.matches(':hover')
    );
}
